import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { addMonths, differenceInMonths, endOfMonth, format, isAfter, isBefore, startOfMonth, subMonths } from 'date-fns';
import { fi } from 'date-fns/locale';
import styles from './styles/SpotPriceFixing.module.scss';

import { OmaLummeApp } from '../../../omaLummeContext';

import Box from '../../Common/Box';
import Info from '../../Common/Info';
import Button from '../../Common/Button';
import Loading from '../../Common/Loading';
import Snackbar from '../../Common/Snackbar';
import SubMenu from '../../Common/SubMenu';

// Actions.
import {
  fetchConsumptionRange,
  fetchProfileCost,
  fetchBasicFeeAndMarginToday,
  fetchPriceFixOffers,
  fetchPriceFixes,
  postNewPriceFix,
} from '../../../actions';
// Types.
import { PriceFixOfferUI, PriceFixUI, BasicFeeAndMarginToday } from '../../../types/contract';
// Utils.
import { roundNumber, setPriceFormat } from '../../../utils/billing';
import { deliveryIsPriceFix } from '../ContractDetails';
import { PriceFixTariffNo } from '../../../utils/contracts';
import { isActiveUserB2B } from '../../../utils/customer';
import { getMonthList } from '../../../utils/consumption';

const dateFormat = 'dd.MM.yyyy';
const dateFormatShort = 'dd.MM.';

const baseUrl = '/spotPriceFixing/new';

const now = new Date();

const OomiLockDownDate = new Date('2026-05-01T00:00:00Z');
const OomiLockDownDateProd = new Date('2026-09-01T00:00:00Z');
const currentDate = new Date();
export const disableNewPriceFixing = currentDate >= OomiLockDownDateProd;

type CalendarProps = {
  month: Date;
  setMonth: (value: React.SetStateAction<Date>) => void;
  history?: PriceFixUI[];
};

const Calendar: React.FunctionComponent<CalendarProps> = (props: CalendarProps) => {
  const { t, i18n } = useTranslation();

  const history = props.history && props.history.length > 0 ? props.history : undefined;
  const historyLast = history ? new Date(history[history.length - 1].priceFixOfferData.Price_end_date) : now;
  const historyFirst = history ? new Date(history[0].priceFixOfferData.Price_start_date) : now;

  const [prevLast, setPrevLast] = React.useState<Date>(historyLast);

  const { month, setMonth } = props;

  const [calendarVisible, setCalendarVisible] = React.useState<boolean>(false);
  const [year, setYear] = React.useState<number>(historyLast.getFullYear());

  React.useEffect(() => {
    if (prevLast.toISOString() !== startOfMonth(historyLast).toISOString()) {
      setPrevLast(startOfMonth(historyLast));
      setMonth(startOfMonth(historyLast));
    }
  }, [historyLast]);

  function useOutsideAlerter(
    ref: React.RefObject<any>,
    open: {
      calendarVisible: boolean;
      setCalendarVisible: (value: React.SetStateAction<boolean>) => void;
    },
  ) {
    React.useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target)) {
          open.setCalendarVisible(false);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, open]);
  }

  const calendarRef = React.useRef<any>(null);
  useOutsideAlerter(calendarRef, { calendarVisible, setCalendarVisible });

  const monthArrow = (next: boolean) => {
    let newMonth = next ? addMonths(month, 1) : subMonths(month, 1);

    const monthDiffExtreme = Math.abs(differenceInMonths(month, next ? historyLast : historyFirst));

    for (let m = 1; m <= monthDiffExtreme; m++) {
      const foundFix = history?.find((f) =>
        next
          ? addMonths(month, m) >= new Date(f.priceFixOfferData.Price_start_date) &&
            addMonths(month, m) <= new Date(f.priceFixOfferData.Price_end_date)
          : subMonths(month, m) >= new Date(f.priceFixOfferData.Price_start_date) &&
            subMonths(month, m) <= new Date(f.priceFixOfferData.Price_end_date),
      );
      if (foundFix) {
        const foundMonth = startOfMonth(new Date(next ? foundFix.priceFixOfferData.Price_start_date : foundFix.priceFixOfferData.Price_end_date));
        if (foundMonth.toISOString() !== month.toISOString() && ((!next && foundMonth < month) || (next && foundMonth > month))) {
          newMonth = foundMonth;
        }
        break;
      }
    }

    setMonth(newMonth);
  };

  return (
    <>
      <div className={styles.datepickerWrapper}>
        <button
          className={styles.prev}
          onClick={(e) => {
            // @ts-ignore
            if (e.target.ariaDisabled === 'false') {
              monthArrow(false);
            }
          }}
          type="button"
          aria-disabled={month <= startOfMonth(historyFirst)}
          aria-label={t(`common:general.previous`)}
        ></button>
        <button
          className={styles.datepicker}
          type="button"
          onClick={() => setCalendarVisible(!calendarVisible)}
          aria-expanded={calendarVisible}
          aria-controls="spotPriceFix-calendar"
        >
          {format(month, 'LLLL yyyy', { locale: fi })}
        </button>
        <button
          className={styles.next}
          onClick={(e) => {
            // @ts-ignore
            if (e.target.ariaDisabled === 'false') {
              monthArrow(true);
            }
          }}
          type="button"
          aria-disabled={month >= startOfMonth(historyLast) || month >= startOfMonth(now)}
          aria-label={t(`common:general.next`)}
        ></button>
      </div>
      {calendarVisible && (
        <div ref={calendarRef} id="spotPriceFix-calendar" className={styles.calendar}>
          <div className={styles.datepickerWrapper}>
            <button
              className={styles.prev}
              onClick={(e) => {
                // @ts-ignore
                if (e.target.ariaDisabled === 'false') {
                  setYear(year - 1);
                }
              }}
              type="button"
              aria-disabled={year <= historyFirst.getFullYear()}
              aria-label={t(`common:general.previous`)}
            ></button>
            <span className={styles.currentYear}>{year}</span>
            <button
              className={styles.next}
              onClick={(e) => {
                // @ts-ignore
                if (e.target.ariaDisabled === 'false') {
                  setYear(year + 1);
                }
              }}
              type="button"
              aria-disabled={year >= historyLast.getFullYear()}
              aria-label={t(`common:general.next`)}
            ></button>
          </div>
          <div className={styles.months}>
            {getMonthList(i18n.language).map((m, i) => {
              const monthObj = new Date(`${year}-${i + 1}-01`);
              return (
                <Button
                  key={m}
                  className="buttonSecondary"
                  onClick={(e) => {
                    // @ts-ignore
                    if (e.target.ariaDisabled === 'false') {
                      setMonth(monthObj);
                    }
                  }}
                  disabled={
                    monthObj > now ||
                    monthObj < historyFirst ||
                    !history?.find(
                      (h) => monthObj >= new Date(h.priceFixOfferData.Price_start_date) && monthObj <= new Date(h.priceFixOfferData.Price_end_date),
                    )
                  }
                >
                  {m}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

type FixingOfferProps = {
  offer: PriceFixOfferUI;
  setOffer: (value: React.SetStateAction<PriceFixOfferUI | undefined>) => void;
  fixed: 0 | 50 | 100 | 5050;
  history: boolean;
  percentage?: number;
  embed?: boolean;
  existingFixings?: PriceFixOfferUI[];
};

const FixingOffer: React.FunctionComponent<FixingOfferProps> = (props: FixingOfferProps) => {
  const { t } = useTranslation();

  const { offer, setOffer, fixed, history, percentage, embed, existingFixings } = props;
  const { users } = OmaLummeApp.useState();

  const duration = differenceInMonths(new Date(offer.Price_end_date), new Date(offer.Price_start_date)) + 1;

  const start = new Date(offer.Price_start_date);
  const end = new Date(offer.Price_end_date);

  const change = offer.change ? setPriceFormat(roundNumber((offer.change - 1) * 100)) : undefined;
  const changePositive = offer.change && offer.change >= 1 ? true : false;

  const params = new URLSearchParams(location.search);

  const isB2B = isActiveUserB2B(users) || (embed && params.get('b2b') === 'true');

  const priceField = isB2B ? 'priceVat0' : 'priceWithVat';

  let price = offer[priceField];

  if (fixed === 5050 && existingFixings?.length === 2) {
    price = (existingFixings[0][priceField] + existingFixings[1][priceField]) / 2;
  }
  if (fixed === 100 && existingFixings?.length === 1) {
    price = existingFixings[0][priceField];
  }

  return (
    <button
      className={`${styles.fixing} ${fixed === 100 || fixed === 5050 ? styles.fixed : undefined} ${history ? styles.history : undefined}`}
      type={fixed === 100 || fixed === 5050 || history ? 'button' : 'submit'}
      onClick={() => {
        setOffer(offer);
      }}
    >
      <div>
        <h3 className={styles.fixingTitle}>
          {format(start, 'LLLL', { locale: fi })}
          {duration > 1 && `–${format(end, 'LLLL', { locale: fi })}`}
        </h3>
        <span>
          {duration} {t(`manageContracts:manageContracts.TablePeriodUnit`)}, {format(start, embed === true ? dateFormatShort : dateFormat)}–
          {format(end, embed === true ? dateFormatShort : dateFormat)}
        </span>
        <div className={styles.price}>
          <>
            <span className={`h2 ${styles.amount}`}>
              {setPriceFormat(roundNumber(price))} <span className={styles.unit}>{t(`consumption:nordPoolGraph.spotUnit`)}</span>
            </span>
            {fixed !== 0 && fixed !== 100 && (!history || (history && percentage === 50)) && (
              <span className={`${styles.percentage} ${!history && fixed === 5050 ? styles.fixed : undefined}`}>
                {fixed === 5050 ? '50+50' : fixed} % {t(`manageContracts:spotPriceFixing.percentageFixing`)}
              </span>
            )}
          </>
          {fixed === 100 && <span className={`${styles.percentage} ${styles.fixed}`}>{t(`manageContracts:spotPriceFixing.percentageFixed`)}</span>}
        </div>
        {fixed !== 100 && fixed !== 5050 && change && (
          <p className={`${styles.change} ${changePositive ? styles.positive : styles.negative}`}>
            {t(`manageContracts:spotPriceFixing.percentageChange`)}{' '}
            <strong>
              {changePositive && '+'}
              {change} %
            </strong>
          </p>
        )}
        {fixed === 5050 && existingFixings?.length === 2 && (
          <Info color="gray" margin="top">
            <Trans
              i18nKey="manageContracts:spotPriceFixing.priceAverage"
              values={{
                price1: setPriceFormat(roundNumber(existingFixings[0][priceField])),
                price2: setPriceFormat(roundNumber(existingFixings[1][priceField])),
              }}
            />
          </Info>
        )}
      </div>
    </button>
  );
};

type SpotPriceFixingType = {
  embed?: boolean;
};

export const SpotPriceFixing: React.FunctionComponent<SpotPriceFixingType> = (props: SpotPriceFixingType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { embed } = props;

  const subLocation = location.pathname.split('/')[2];

  const { config, users, selectedMeteringPoint, selectedMeteringPointContract } = OmaLummeApp.useState();

  const [activePage, setActivePage] = React.useState<number>(1);

  const [offers, setOffers] = React.useState<PriceFixOfferUI[] | undefined>(undefined);
  const [fixings, setFixings] = React.useState<PriceFixUI[] | undefined>(undefined);
  const [basicFeeAndMargin, setBasicFeeAndMargin] = React.useState<BasicFeeAndMarginToday[] | undefined>(undefined);

  const [loadingOffers, setLoadingOffers] = React.useState<boolean>(false);
  const [loadingFixings, setLoadingFixings] = React.useState<boolean>(false);

  const [selectedOffer, setSelectedOffer] = React.useState<PriceFixOfferUI | undefined>(undefined);
  const [selectedPercentage, setSelectedPercentage] = React.useState<number | undefined>(undefined);

  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);

  const [formSubmitting, setFormSubmitting] = React.useState<boolean>(false);
  const [formSubmitError, setFormSubmitError] = React.useState<boolean>(false);

  const start = selectedOffer ? new Date(selectedOffer.Price_start_date) : undefined;
  const end = selectedOffer ? new Date(selectedOffer.Price_end_date) : undefined;

  const [month, setMonth] = React.useState<Date>(startOfMonth(now));

  const [consumptionImpact, setConsumptionImpact] = React.useState<number | undefined>(undefined);
  const [realizedCost, setRealizedCost] = React.useState<number | undefined>(undefined);

  const [newFixing, setNewFixing] = React.useState<string | undefined>(undefined);

  const params = new URLSearchParams(location.search);

  const isB2B = isActiveUserB2B(users) || (embed && params.get('b2b') === 'true');

  const priceField = isB2B ? 'priceVat0' : 'priceWithVat';

  React.useEffect(() => {
    setOffers(undefined);
    setFixings(undefined);
  }, [users.activeUser, selectedMeteringPoint]);

  React.useEffect(() => {
    async function fetchImpact() {
      if (selectedMeteringPointContract) {
        setConsumptionImpact(undefined);
        const profileCostData = await fetchProfileCost(
          selectedMeteringPointContract,
          selectedMeteringPointContract.customerId,
          startOfMonth(month).toISOString(),
          endOfMonth(month).toISOString(),
        );
        if (profileCostData && profileCostData.length > 0) {
          setConsumptionImpact(profileCostData[0][isB2B ? 'profilePrice' : 'profilePriceWithVat']);
        }
      }
    }
    async function fetchRealized() {
      if (selectedMeteringPoint && selectedMeteringPointContract) {
        setRealizedCost(undefined);
        const monthConsumption = await fetchConsumptionRange(
          selectedMeteringPointContract.customerId,
          users.activeUser.userId,
          selectedMeteringPoint?.meteringPointId,
          'year',

          startOfMonth(month).toISOString(),
          endOfMonth(month).toISOString(),
        );
        if (monthConsumption && monthConsumption.items.length > 0) {
          setRealizedCost(monthConsumption.items[0][isB2B ? 'realizedCostWithoutVat' : 'realizedCostWithVat']);
        }
      }
    }

    fetchImpact();
    fetchRealized();
  }, [month]);

  React.useEffect(() => {
    async function getOffers(): Promise<void> {
      if (selectedMeteringPointContract || embed === true) {
        setLoadingOffers(true);
        const fixOffers = await fetchPriceFixOffers();
        if (fixOffers) {
          setLoadingOffers(false);
        }
        setOffers(fixOffers.sort((a, b) => new Date(a.Price_start_date).getTime() - new Date(b.Price_start_date).getTime()));
      }
    }
    async function getFixings(): Promise<void> {
      if (selectedMeteringPointContract) {
        setLoadingFixings(true);
        const existingFixings = await fetchPriceFixes(
          selectedMeteringPointContract.customerId,
          users.activeUser.userId,
          selectedMeteringPointContract.contractCode,
        );

        const sortedFixings = existingFixings
          .filter((f) => f.meteringPointId === selectedMeteringPoint?.meteringPointId)
          .sort((a, b) => new Date(a.priceFixOfferData.Price_start_date).getTime() - new Date(b.priceFixOfferData.Price_start_date).getTime());
        if (sortedFixings) {
          setFixings(sortedFixings);
          setLoadingFixings(false);
        }
      }
    }
    async function fetchBasicFeeAndMargin(): Promise<void> {
      if (selectedMeteringPointContract) {
        const fetchedFeeAndMargin = await fetchBasicFeeAndMarginToday();

        setBasicFeeAndMargin(fetchedFeeAndMargin);
      } else {
        setBasicFeeAndMargin(undefined);
      }
    }

    getOffers();
    getFixings();

    fetchBasicFeeAndMargin();
  }, [selectedMeteringPointContract, users.activeUser, embed, newFixing]);

  const offerYears: number[] = [];
  offers?.forEach((o) => {
    if (!offerYears.includes(o.Year)) {
      offerYears.push(o.Year);
    }
  });
  const fixingYears: number[] = [];
  fixings?.forEach((f) => {
    if (!fixingYears.includes(f.priceFixOfferData.Year)) {
      fixingYears.push(f.priceFixOfferData.Year);
    }
  });

  React.useEffect(() => {
    const navButtons = document.querySelectorAll('nav#spotPriceFixNav button');

    if (navButtons[activePage - 1]) {
      (navButtons[activePage - 1] as HTMLButtonElement).focus();
    }
  }, [activePage]);

  window.addEventListener('hashchange', function () {
    if (selectedOffer && selectedPercentage && termsAccepted && window.location.hash === '#thanks') {
      setActivePage(4);
    } else if (selectedOffer && selectedPercentage && window.location.hash === '#summary') {
      setActivePage(3);
    } else if (selectedOffer && window.location.hash === '#percentage') {
      setActivePage(2);
    } else {
      setActivePage(1);
    }
  });

  const submitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (activePage === 1) {
      setActivePage(2);
      navigate(`${baseUrl}#percentage`);
    } else if (activePage === 2) {
      setActivePage(3);
      navigate(`${baseUrl}#summary`);
    } else if (activePage === 3) {
      if (selectedMeteringPointContract && selectedMeteringPointContract.meteringPointId && selectedOffer && selectedPercentage) {
        setFormSubmitting(true);
        setFormSubmitError(false);

        const fixPrice = await postNewPriceFix(
          selectedMeteringPointContract?.customerId,
          users.activeUser.userId,
          selectedOffer,
          selectedMeteringPointContract,
          selectedMeteringPointContract.meteringPointId,
          selectedPercentage,
        );

        setFormSubmitting(false);

        if (fixPrice === 201) {
          setNewFixing(`${users.activeUser.userId}_${selectedMeteringPointContract.contractCode}_${selectedOffer.Price_id}_${selectedPercentage}`);
          setActivePage(4);
          navigate(`${baseUrl}#thanks`);
        } else {
          setFormSubmitError(true);
        }
      }
    }
  };

  const existingFixings50 = fixings?.filter(
    (f) =>
      selectedMeteringPointContract?.customerId.toString() === f.customerId &&
      selectedMeteringPointContract?.contractCode === f.contractCode &&
      f.priceFixOfferData.Price_start_date === selectedOffer?.Price_start_date &&
      f.priceFixOfferData.Price_end_date === selectedOffer?.Price_end_date &&
      f.percentage === 50,
  );
  const existingFixing100 = fixings?.find(
    (f) =>
      selectedMeteringPointContract?.customerId.toString() === f.customerId &&
      selectedMeteringPointContract?.contractCode === f.contractCode &&
      f.priceFixOfferData.Price_start_date === selectedOffer?.Price_start_date &&
      f.priceFixOfferData.Price_end_date === selectedOffer?.Price_end_date &&
      f.percentage === 100,
  );
  const existingFixing50 = existingFixings50?.length === 1;

  const marginElement = basicFeeAndMargin ? basicFeeAndMargin.find((i) => i.priceText === 'Margin') : undefined;
  const margin = marginElement ? marginElement[isB2B ? 'price' : 'priceInclVat'] : undefined;

  const currentFixings = fixings?.filter(
    (f) => now >= new Date(f.priceFixOfferData.Price_start_date) && now <= new Date(f.priceFixOfferData.Price_end_date),
  );

  const pastFixings = fixings?.filter((f) => isAfter(now, new Date(f.priceFixOfferData.Price_start_date)));

  const pastFixingsInSelectedMonth = fixings?.filter(
    (f) => month >= new Date(f.priceFixOfferData.Price_start_date) && month <= new Date(f.priceFixOfferData.Price_end_date),
  );

  if (month.toISOString() === startOfMonth(now).toISOString() && pastFixings && pastFixings.length > 0 && pastFixingsInSelectedMonth?.length === 0) {
    setMonth(
      startOfMonth(
        new Date(
          pastFixings.sort((a, b) => (a.priceFixOfferData.Price_start_date < b.priceFixOfferData.Price_start_date ? 1 : -1))[0].priceFixOfferData
            .Price_end_date,
        ),
      ),
    );
  }

  const pastMonthAverage =
    pastFixingsInSelectedMonth && pastFixingsInSelectedMonth.length > 1
      ? pastFixingsInSelectedMonth.reduce((a, b) => a + b.priceFixOfferData[priceField], 0) / pastFixingsInSelectedMonth.length
      : undefined;

  const currentFixingsAverage =
    currentFixings && currentFixings.length > 1
      ? currentFixings.reduce((a, b) => a + b.priceFixOfferData[priceField], 0) / currentFixings.length
      : undefined;

  if (
    embed !== true &&
    (config?.priceFixing === false ||
      (config?.priceFixing === true &&
        selectedMeteringPointContract &&
        selectedMeteringPointContract.deliveries &&
        selectedMeteringPointContract.deliveries.filter((d) => d.tariffNo === PriceFixTariffNo).length === 0))
  ) {
    navigate('/');
  }

  const currentContractIsPriceFixAndActive = selectedMeteringPointContract?.deliveries?.find(
    (d) => deliveryIsPriceFix(d) && new Date(d.tariffFromDate) <= new Date() && new Date(d.tariffToDate) >= new Date(),
  )
    ? true
    : false;

  // Lumme Energia is part of Oomi: new price fixings cannot be made after the lock down date.
  if (embed !== true && disableNewPriceFixing && subLocation === 'new') {
    return null;
  }

  return (
    <>
      {embed === true && (
        <>
          {loadingOffers && <Loading />}
          {offerYears.map((year) => (
            <div key={year} className={`${styles.fixingWrapper} ${styles.fixingWrapperEmbed}`}>
              <h2>{year}</h2>

              {offers
                ?.filter((o) => o.Year === year)
                .map((o) => (
                  <FixingOffer key={o.Price_id} offer={o} setOffer={setSelectedOffer} fixed={0} history={true} embed={true} />
                ))}
            </div>
          ))}
        </>
      )}
      {embed !== true && (
        <>
          {subLocation === 'new' && (
            <form onSubmit={submitForm}>
              <Box>
                <div className={styles.navWrapper}>
                  {activePage === 1 && (
                    <Link to="/spotPriceFixing" className={styles.navBack}>
                      {t(`common:general.back`)}
                    </Link>
                  )}
                  {activePage > 1 && activePage < 4 && (
                    <button
                      className={styles.navBack}
                      type="button"
                      onClick={() => {
                        if (activePage === 2) {
                          setActivePage(1);
                          navigate(`${baseUrl}`);
                        } else if (activePage === 3) {
                          setActivePage(2);
                          navigate(`${baseUrl}#percentage`);
                        }
                      }}
                    >
                      {t(`common:general.back`)}
                    </button>
                  )}
                  <nav id="spotPriceFixNav" className={styles.navigation}>
                    <button
                      className={activePage > 1 ? styles.finished : styles.active}
                      type="button"
                      aria-disabled={activePage > 1 && activePage < 4 ? false : true}
                      aria-current={activePage === 1 ? 'step' : false}
                      onClick={(e) => {
                        // @ts-ignore
                        if (e.target.ariaDisabled === 'false') {
                          setActivePage(1);
                          navigate(`${baseUrl}`);
                        }
                      }}
                    >
                      {t(`manageContracts:spotPriceFixing.navPeriod`)}
                    </button>
                    <button
                      className={activePage === 2 ? styles.active : activePage > 2 ? styles.finished : undefined}
                      type="button"
                      aria-disabled={activePage > 2 && activePage < 4 ? false : true}
                      aria-current={activePage === 2 ? 'step' : false}
                      onClick={(e) => {
                        // @ts-ignore
                        if (e.target.ariaDisabled === 'false') {
                          setActivePage(2);
                          navigate(`${baseUrl}#percentage`);
                        }
                      }}
                    >
                      {t(`manageContracts:spotPriceFixing.navPercentage`)}
                    </button>
                    <button
                      className={activePage === 3 ? styles.active : activePage > 3 ? styles.finished : undefined}
                      type="button"
                      aria-disabled="true"
                      aria-current={activePage === 3 ? 'step' : false}
                    >
                      {t(`manageContracts:spotPriceFixing.navSummary`)}
                    </button>
                  </nav>

                  {activePage < 4 && <span className={styles.empty}></span>}
                </div>
              </Box>
              {activePage === 1 && (
                <Box>
                  <h1>1. {t(`manageContracts:spotPriceFixing.periodTitle`)}</h1>

                  <p>
                    {t(`manageContracts:spotPriceFixing.periodDescription`)}{' '}
                    <a>{t(`manageContracts:spotPriceFixing.periodDescriptionImpactLink`)}.</a>
                  </p>

                  {!currentContractIsPriceFixAndActive && <p>{t(`manageContracts:spotPriceFixing.priceFixNotActiveForCurrentContract`)}</p>}

                  {loadingFixings && <Loading />}

                  {currentContractIsPriceFixAndActive &&
                    fixings !== undefined &&
                    offerYears.map((year) => (
                      <div key={year} className={styles.fixingWrapper}>
                        <h2>{year}</h2>

                        {offers
                          ?.filter((o) => o.Year === year)
                          .map((o) => {
                            const existingFixings = fixings.filter(
                              (f) =>
                                selectedMeteringPointContract?.customerId.toString() === f.customerId &&
                                selectedMeteringPointContract.contractCode === f.contractCode &&
                                new Date(f.priceFixOfferData.Price_start_date) <= new Date(o.Price_start_date) &&
                                new Date(f.priceFixOfferData.Price_end_date) >= new Date(o.Price_end_date),
                            );

                            let fixed: 0 | 50 | 100 | 5050 = 0;

                            if (existingFixings.filter((f) => f.percentage === 50).length === 2) {
                              fixed = 5050;
                            } else if (existingFixings.filter((f) => f.percentage === 50).length === 1) {
                              fixed = 50;
                            } else if (existingFixings.filter((f) => f.percentage === 100).length === 1) {
                              fixed = 100;
                            }

                            return (
                              <FixingOffer
                                key={o.Price_id}
                                offer={o}
                                setOffer={setSelectedOffer}
                                fixed={fixed}
                                history={false}
                                existingFixings={existingFixings.map((e) => e.priceFixOfferData)}
                              />
                            );
                          })}
                      </div>
                    ))}
                </Box>
              )}
              {activePage === 2 && (
                <Box>
                  <h1>2. {t(`manageContracts:spotPriceFixing.percentageTitle`)}</h1>

                  <div className={styles.fixingWrapper}>
                    <button
                      className={`${styles.fixing} ${styles.percentage} ${styles.percentage50}`}
                      type="submit"
                      onClick={() => setSelectedPercentage(50)}
                    >
                      <div>
                        <h3 className={styles.fixingTitle}>
                          {existingFixing50 && <>{t(`manageContracts:spotPriceFixing.percentageFixingRest`)} 50 %</>}
                          {!existingFixing50 && (
                            <>
                              50 % {t(`manageContracts:spotPriceFixing.percentageFixingFix`)}
                              <br />
                              50 % {t(`manageContracts:spotPriceFixing.percentageFixingSpot`)}
                            </>
                          )}
                        </h3>

                        <p>{t(`manageContracts:spotPriceFixing.percentageFixingDescription50${existingFixing50 ? `Rest` : ''}`)}</p>
                      </div>
                    </button>
                    {!existingFixing50 && !existingFixing100 && (
                      <button
                        className={`${styles.fixing} ${styles.percentage} ${styles.percentage100}`}
                        type="submit"
                        onClick={() => setSelectedPercentage(100)}
                      >
                        <div>
                          <h3 className={styles.fixingTitle}>100 % {t(`manageContracts:spotPriceFixing.percentageFixingFix`)}</h3>

                          <p>{t(`manageContracts:spotPriceFixing.percentageFixingDescription100`)}</p>
                        </div>
                      </button>
                    )}
                  </div>

                  <Info margin="top">
                    {t(`manageContracts:spotPriceFixing.percentageInfo`)}{' '}
                    <a href="https://www.lumme-energia.fi/kulutusvaikutus" target="_blank" rel="noopener noreferrer">
                      {t(`common:general.readMore`)}
                    </a>
                  </Info>
                </Box>
              )}
              {activePage === 3 && (
                <>
                  <Box>
                    <h1>3. {t(`manageContracts:spotPriceFixing.summaryTitle`)}</h1>

                    <div className={styles.summary}>
                      <span className={styles.subTitle}>{t(`manageContracts:spotPriceFixing.summarySubTitle`)}</span>
                      <h3>
                        {format(start || new Date(), 'LLLL', { locale: fi })}–{format(end || new Date(), 'LLLL yyyy', { locale: fi })}{' '}
                        {selectedPercentage} %
                      </h3>
                      <p>{t(`manageContracts:spotPriceFixing.summaryDescription`)}</p>
                      <div className={styles.summaryRow}>
                        <span>{t(`manageContracts:spotPriceFixing.summaryPeriod`)}</span>
                        {format(start || new Date(), dateFormat)}–{format(end || new Date(), dateFormat)}
                      </div>
                      <div className={styles.summaryRow}>
                        <span>{t(`manageContracts:spotPriceFixing.summaryEnergyPrice`)}</span>
                        {setPriceFormat(roundNumber(selectedOffer ? selectedOffer[priceField] : 0))} {t(`consumption:nordPoolGraph.spotUnit`)}
                      </div>
                      <div className={styles.summaryRow}>
                        <span>{t(`manageContracts:spotPriceFixing.summaryConsumptionImpact`)}</span>
                        {t(`manageContracts:spotPriceFixing.summaryConsumptionImpactEstimate`)} ± 0–3 {t(`consumption:nordPoolGraph.spotUnit`)}
                      </div>
                      <div className={styles.summaryRow}>
                        <span>{t(`manageContracts:spotPriceFixing.summaryEnergyPriceCovers`)}</span>
                        {selectedPercentage} % {t(`manageContracts:spotPriceFixing.summaryEnergyPriceCoversOfConsumption`)}
                      </div>
                      {margin && (
                        <div className={styles.summaryRow}>
                          <span>{t(`manageContracts:spotPriceFixing.summarySpotMargin`)}</span>
                          {setPriceFormat(roundNumber(margin))} {t(`consumption:nordPoolGraph.spotUnit`)}
                        </div>
                      )}
                      <Info margin="top">{t(`consumption:consumption.${isB2B ? 'noIncTax' : 'incTax'}`)}</Info>
                    </div>

                    <div className={`form-item checkbox ${styles.checkbox}`}>
                      <input type="checkbox" id="spotPriceAgreement" onChange={() => setTermsAccepted(!termsAccepted)} checked={termsAccepted} />
                      <div className="form-item-input"></div>
                      <label htmlFor="spotPriceAgreement">
                        <p>
                          <Trans
                            i18nKey="manageContracts:spotPriceFixing.summaryCheckboxLabel"
                            values={{
                              terms: t(`manageContracts:spotPriceFixing.summaryCheckboxLabelTerms`),
                              privacy: t(`manageContracts:spotPriceFixing.summaryCheckboxLabelPrivacy`),
                            }}
                          >
                            .
                            <a
                              href="https://www.lumme-energia.fi/sahkosopimus/porssisahko-hintaturva/sopimusehdot"
                              target="_blank"
                              rel="noopener noreferrer"
                            ></a>
                            <a href="https://www.lumme-energia.fi/tietosuojaseloste" target="_blank" rel="noopener noreferrer"></a>
                          </Trans>
                        </p>
                      </label>
                    </div>
                    <div className={styles.submitWrapper}>
                      <Button
                        type="submit"
                        disabled={!termsAccepted}
                        onClick={(e) => {
                          // @ts-ignore
                          if (e.target.ariaDisabled !== 'false') {
                            e.preventDefault();
                          }
                        }}
                      >
                        {t(`manageContracts:spotPriceFixing.summarySubmit`)}
                      </Button>
                    </div>

                    {formSubmitting && (
                      <>
                        <br />
                        <Loading size="small" />
                      </>
                    )}
                  </Box>

                  {formSubmitError && (
                    <Snackbar type="error" margin="top">
                      {t(`manageContracts:spotPriceFixing.fixingFailed`)}
                    </Snackbar>
                  )}
                </>
              )}
              {activePage === 4 && (
                <Box>
                  <div className={styles.success}>
                    <h1>{t(`manageContracts:spotPriceFixing.successTitle`)}</h1>
                    <p>{t(`manageContracts:spotPriceFixing.successDescription`)}</p>
                    <div className={styles.buttons}>
                      <Button link="/spotPriceFixing" onClick={() => setActivePage(1)}>
                        {t(`manageContracts:spotPriceFixing.buttonFixHistory`)}
                      </Button>
                      <Button className="buttonSecondary" link="/" onClick={() => setActivePage(1)}>
                        {t(`manageContracts:spotPriceFixing.buttonHome`)}
                      </Button>
                    </div>
                  </div>
                </Box>
              )}
            </form>
          )}

          {subLocation !== 'new' && (
            <>
              <Box>
                <h1>{t(`manageContracts:spotPriceFixing.homeTitle`)}</h1>
                {disableNewPriceFixing ? (
                  <p>
                    <Trans
                      i18nKey="manageContracts:spotPriceFixing.homeDescriptionOomi"
                      values={{
                        priceFixUrl: 'https://www.lumme-energia.fi/hinnankiinnitykset',
                        readMoreUrl: 'https://www.lumme-energia.fi/oomi',
                      }}
                    >
                      .
                      <a href="https://www.lumme-energia.fi/hinnankiinnitykset" target="_blank" rel="noopener noreferrer"></a>
                      <a href="https://www.lumme-energia.fi/oomi" target="_blank" rel="noopener noreferrer"></a>
                    </Trans>
                  </p>
                ) : (
                  <p>{t(`manageContracts:spotPriceFixing.homeDescription`)}</p>
                )}

                {!currentContractIsPriceFixAndActive && <p>{t(`manageContracts:spotPriceFixing.priceFixNotActiveForCurrentContract`)}</p>}
                {currentContractIsPriceFixAndActive && !disableNewPriceFixing && (
                  <Button link="/spotPriceFixing/new">{t(`manageContracts:spotPriceFixing.homeButtonNew`)}</Button>
                )}
                <Button className="buttonSecondary" link="/contracts">
                  {t(`manageContracts:spotPriceFixing.homeButtonContractDetails`)}
                </Button>
              </Box>
              {currentContractIsPriceFixAndActive && fixings !== undefined && fixings.length > 0 && (
                <Box>
                  <SubMenu />

                  {subLocation === undefined && (
                    <div className={styles.timeperiodFixings}>
                      {!currentFixings || (currentFixings.length === 0 && <p>{t(`manageContracts:spotPriceFixing.noCurrentFixings`)}</p>)}
                      {currentFixings &&
                        currentFixings.map((f) => (
                          <FixingOffer
                            key={f.priceFixUniqueCode}
                            offer={f.priceFixOfferData}
                            setOffer={setSelectedOffer}
                            fixed={f.percentage === 50 ? 50 : 0}
                            history={true}
                            percentage={f.percentage}
                          />
                        ))}

                      {currentFixingsAverage && (
                        <div className={styles.dashedTop}>
                          <h4 className={styles.fixingTitle}>{t(`manageContracts:spotPriceFixing.average`)}</h4>
                          <div className={styles.price}>
                            <span className={`h2 ${styles.amount}`}>
                              {setPriceFormat(roundNumber(currentFixingsAverage))}{' '}
                              <span className={styles.unit}>{t(`consumption:nordPoolGraph.spotUnit`)}</span>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {subLocation === 'upcoming' &&
                    fixingYears
                      .filter((y) => y >= now.getFullYear())
                      .map((year) => (
                        <div key={year} className={`${styles.fixingWrapper} ${styles.upcoming}`}>
                          <h2>{year}</h2>

                          {fixings?.filter((f) => f.priceFixOfferData.Year === year && isBefore(now, new Date(f.priceFixOfferData.Price_start_date)))
                            .length === 0 && <p>{t(`manageContracts:spotPriceFixing.noUpcomingFixings`)}</p>}

                          {fixings
                            ?.filter((f) => f.priceFixOfferData.Year === year && isBefore(now, new Date(f.priceFixOfferData.Price_start_date)))
                            .map((f) => {
                              const existingFixings = fixings.filter(
                                (ef) => ef.priceFixOfferData.Year === year && ef.priceFixOfferData.Quarter === f.priceFixOfferData.Quarter,
                              );

                              let fixed: 0 | 50 | 100 | 5050 = 0;
                              if (existingFixings.filter((f) => f.percentage === 50).length === 2) {
                                fixed = 5050;
                              } else if (existingFixings.filter((f) => f.percentage === 50).length === 1) {
                                fixed = 50;
                              }

                              const fixings50 = existingFixings.filter((ef) => ef.percentage === 50);

                              if (fixings50.length > 1 && f.percentage === 50 && f.priceFixUniqueCode === fixings50[1].priceFixUniqueCode) {
                                return false;
                              }

                              return (
                                <FixingOffer
                                  key={f.priceFixOfferId}
                                  offer={f.priceFixOfferData}
                                  setOffer={setSelectedOffer}
                                  fixed={fixed}
                                  history={true}
                                  percentage={f.percentage}
                                  existingFixings={existingFixings.map((e) => e.priceFixOfferData)}
                                />
                              );
                            })}
                        </div>
                      ))}
                  {subLocation === 'history' && (
                    <>
                      {!pastFixings ||
                        (pastFixings.length === 0 && (
                          <div className={styles.timeperiodFixings}>
                            <p>{t(`manageContracts:spotPriceFixing.noPastFixings`)}</p>
                          </div>
                        ))}
                      {pastFixings && pastFixings.length > 0 && (
                        <>
                          <Calendar history={pastFixings} month={month} setMonth={setMonth} />
                          <div className={styles.history}>
                            <div className={styles.timeperiodFixings}>
                              {pastFixingsInSelectedMonth?.map((f) => (
                                <FixingOffer
                                  key={f.priceFixUniqueCode}
                                  offer={f.priceFixOfferData}
                                  setOffer={setSelectedOffer}
                                  fixed={f.percentage === 50 ? 50 : 0}
                                  history={true}
                                  percentage={f.percentage}
                                />
                              ))}
                              {pastMonthAverage && (
                                <div className={styles.dashedTop}>
                                  <h4 className={styles.fixingTitle}>{t(`manageContracts:spotPriceFixing.average`)}</h4>
                                  <div className={styles.price}>
                                    <span className={`h2 ${styles.amount}`}>
                                      {setPriceFormat(roundNumber(pastMonthAverage))}{' '}
                                      <span className={styles.unit}>{t(`consumption:nordPoolGraph.spotUnit`)}</span>
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                            {consumptionImpact && (
                              <div>
                                <div className={`${styles.timeperiodFixings} ${styles.priceAnalysis}`}>
                                  <div>
                                    <h3 className={styles.fixingTitle}>{t(`manageContracts:spotPriceFixing.consumptionImpact`)}</h3>
                                    <div className={styles.price}>
                                      <span className={`h2 ${styles.amount}`}>
                                        {setPriceFormat(roundNumber(consumptionImpact))}{' '}
                                        <span className={styles.unit}>{t(`consumption:nordPoolGraph.spotUnit`)}</span>
                                      </span>
                                    </div>
                                    {consumptionImpact < 0 && (
                                      <span className={`${styles.impact} ${styles.impactLow}`}>
                                        {t(`manageContracts:spotPriceFixing.impactLoweredPrice`)}
                                      </span>
                                    )}
                                    {consumptionImpact > 0 && (
                                      <span className={`${styles.impact} ${styles.impactHigh}`}>
                                        {t(`manageContracts:spotPriceFixing.impactRaisedPrice`)}
                                      </span>
                                    )}
                                    {consumptionImpact === 0 && (
                                      <span className={`${styles.impact} ${styles.impactNeutral}`}>
                                        {t(`manageContracts:spotPriceFixing.impactNeutralPrice`)}
                                      </span>
                                    )}
                                  </div>

                                  {realizedCost !== undefined && (
                                    <div className={styles.dashedTop}>
                                      <h3 className={styles.fixingTitle}>{t(`manageContracts:spotPriceFixing.realizedCost`)}</h3>
                                      <div className={styles.price}>
                                        <span className={`h2 ${styles.amount}`}>
                                          {setPriceFormat(roundNumber(realizedCost))}{' '}
                                          <span className={styles.unit}>{t(`consumption:nordPoolGraph.spotUnit`)}</span>
                                        </span>
                                      </div>
                                      <div className={styles.realizedInfo}>
                                        <Info margin="top">{t(`manageContracts:spotPriceFixing.realizedCostDisclaimer`)}</Info>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}

              {loadingFixings && <Loading />}
            </>
          )}
        </>
      )}
    </>
  );
};

export default SpotPriceFixing;
